import { useId } from 'react';
import type { WorkAreaGateType } from '@/utils/workAreaPermissionGate';

/**
 * I-1678 (SB-9, `docs/concept-ownership-organisations-and-instances.md` §10.12): a refused control's
 * reason, named by reference. `gateControlProps` puts it in the control's `title`; a screen reader
 * may or may not read a title as the description, so a refused control is also `aria-describedby`
 * its reason. A tap on it shows the reason through `RefusedTouchHint` (F-0324), which every refused
 * control already gets from the app's root, so nothing here draws it a second time.
 *
 * `controlProps` go on every control the gate refuses, after `gateControlProps`.
 */
export function useRefusalNote(gate: WorkAreaGateType) {
  const id = useId();
  const reason = gate.state === 'refused' ? gate.reason : null;
  return { id, reason, controlProps: reason ? { 'aria-describedby': id } : {} };
}

/** The description `aria-describedby` names: never drawn, read by a screen reader. */
export function RefusalDescription({ id, reason }: { id: string; reason: string | null }) {
  if (!reason) return null;
  return (
    <span id={id} hidden>
      {reason}
    </span>
  );
}
